import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useStoreDiscountDetails, useOtp } from 'modules/transactions/new-sales-entry/services/use-sales-entry.service';
import { useSalesEntry } from 'contexts/SalesEntryContext';

interface DiscountModalProps {
  show: boolean;
  onHide: () => void;
}

interface FormErrors {
  percentage?: string;
  amount?: string;
  otp?: string;
  otpId?: string;
}

interface FormData {
  percentage: string;
  amount: string;
  otp: string;
  otpId?: string;
}

export const DiscountModal: React.FC<DiscountModalProps> = ({
  show,
  onHide
}) => {
  const { state, actions } = useSalesEntry();
  const { getStoreDiscountDetails, discountDetails, loading: discountLoading } = useStoreDiscountDetails();
  const { sendOtp, verifyOtp, loading: otpApiLoading, error: otpError, resetOtp } = useOtp();

  const [formData, setFormData] = useState<FormData>({
    percentage: '',
    amount: '',
    otp: '',
    otpId: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [phoneNumber] = useState('7262966609');
  const [resendTimer, setResendTimer] = useState(0);
  const [originalPercentage, setOriginalPercentage] = useState<number>(0);
  const [inputMethod, setInputMethod] = useState<'percentage' | 'amount' | null>(null);

  useEffect(() => {
    if (show) {
      getStoreDiscountDetails();
    }
  }, [show, getStoreDiscountDetails]);

  useEffect(() => {
    if (show && state.discount > 0) {
      const ornamentAmt = state.summary.ornamentAmt;
      const percentage = ornamentAmt > 0 ? ((state.discount / ornamentAmt) * 100) : 0;

      setOriginalPercentage(percentage);
      // setFormData({
      //   percentage: percentage.toFixed(2),
      //   amount: state.discount.toString(),
      //   otp: '',
      //   otpId: ''
      // });
      // setInputMethod('percentage');
      const consumerOffer = state.summary?.consumerOffer || 0;
      const discountVoucher = state.summary?.discountVoucher || 0;
      const baseAmount = ornamentAmt - consumerOffer - discountVoucher;
      if (state.discount > 0 && baseAmount > 0) {
        const isPercentage = state.discountType === 'percentage';
        const percentage = (state.discount / baseAmount) * 100;
        const amount = state.discount;

        setFormData({
          percentage: isPercentage ? percentage.toFixed(2) : '',
          amount: !isPercentage ? amount.toFixed(2) : '',
          otp: '',
          otpId: ''
        });

        setInputMethod(isPercentage ? 'percentage' : 'amount');
        setOriginalPercentage(percentage);
      }

      if (percentage > (discountDetails?.discountLimit || 0)) {
        setOtpVerified(true);
        setShowOtpSection(false);
        setOtpSent(false);
      } else {
        setOtpVerified(false);
        setShowOtpSection(false);
        setOtpSent(false);
      }
    } else if (show && state.discount === 0) {
      setOriginalPercentage(0);
      setFormData({
        percentage: '',
        amount: '',
        otp: '',
        otpId: ''
      });
      setInputMethod(null);
      setOtpVerified(false);
      setShowOtpSection(false);
      setOtpSent(false);
    }
    setErrors({});
  }, [show, state.discount, state.summary.ornamentAmt, discountDetails]);

  const validateDiscountForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (inputMethod === 'percentage' && !formData.percentage.trim()) {
      newErrors.percentage = 'Discount percentage is required';
    } else if (inputMethod === 'percentage' && isNaN(Number(formData.percentage))) {
      newErrors.percentage = 'Invalid percentage';
    } else if (inputMethod === 'percentage') {
      const percentage = Number(formData.percentage);
      if (percentage < 0) newErrors.percentage = 'Percentage cannot be negative';
      if (percentage > 100) newErrors.percentage = 'Percentage cannot exceed 100%';
      // if (discountDetails && percentage > discountDetails.discountFreez) {
      //   newErrors.percentage = `Maximum allowed percentage is ${discountDetails.discountFreez}%`;
      // }
    }

    if (inputMethod === 'amount' && !formData.amount.trim()) {
      newErrors.amount = 'Discount amount is required';
    } else if (inputMethod === 'amount' && isNaN(Number(formData.amount))) {
      newErrors.amount = 'Invalid amount';
    } else if (inputMethod === 'amount') {
      const amount = Number(formData.amount);
      if (amount < 0) newErrors.amount = 'Amount cannot be negative';
      if (amount > state.summary.ornamentAmt) {
        newErrors.amount = `Amount cannot exceed ornament value (₹${state.summary.ornamentAmt})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const needsOtpVerification = (): boolean => {
    if (!discountDetails) return false;

    if (inputMethod === 'percentage') {
      const percentage = Number(formData.percentage);
      const isChangingFromExisting = originalPercentage > 0 && Math.abs(percentage - originalPercentage) > 0.01;
      const exceedsLimit = percentage > discountDetails.discountLimit;
      return exceedsLimit && (originalPercentage === 0 || isChangingFromExisting);
    } else if (inputMethod === 'amount') {
      const percentage = (Number(formData.amount) / state.summary.ornamentAmt) * 100;
      const isChangingFromExisting = originalPercentage > 0 && Math.abs(percentage - originalPercentage) > 0.01;
      const exceedsLimit = percentage > discountDetails.discountLimit;
      return exceedsLimit && (originalPercentage === 0 || isChangingFromExisting);
    }
    return false;
  };

  const validateOtp = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (formData.otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDiscountSubmit = async () => {
    if (!validateDiscountForm()) return;

    if (needsOtpVerification() && !otpVerified) {
      setShowOtpSection(true);
    } else {
      actions.setDiscount(Number(inputMethod === 'percentage'
        ? ((state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher) * Number(formData.percentage) / 100)
        : formData.amount
      ), inputMethod ?? undefined);
    }
  };

  const handleSendOtp = async () => {
    try {
      const result = await sendOtp(phoneNumber, 'Discount');
      if (result?.status === 200 && result.data?.otpId) {
        setFormData(prev => ({ ...prev, otpId: result.data!.otpId }));
        setOtpSent(true);
        setResendTimer(30);
        setErrors(prev => ({ ...prev, otp: '' }));
      } else {
        setErrors(prev => ({ ...prev, otp: otpError || 'Failed to send OTP' }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, otp: 'Failed to send OTP' }));
    }
  };

  const handleOtpVerification = async () => {
    if (!validateOtp()) return;

    const result = await verifyOtp(formData.otp, 'Discount');
    if (result?.status === 200) {
      setOtpVerified(true);
      setShowOtpSection(false);
      actions.setDiscount(Number(inputMethod === 'percentage'
        ? ((state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher) * Number(formData.percentage) / 100)
        : formData.amount
      ), inputMethod ?? undefined);
    } else {
      setErrors({ otp: otpError || 'Invalid OTP. Please try again.' });
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer === 0) {
      await handleSendOtp();
      setFormData(prev => ({ ...prev, otp: '' }));
    }
  };

  const handleRemove = () => {
    actions.setDiscount(0);
    onHide();
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));

    // Set the input method when a field is modified
    if (field === 'percentage' && value.trim() !== '') {
      setInputMethod('percentage');
    } else if (field === 'amount' && value.trim() !== '') {
      setInputMethod('amount');
    }

    if (field === 'percentage' && discountDetails) {
      const percentage = Number(value);
      if (!isNaN(percentage)) {
        const isChangingFromExisting = originalPercentage > 0 && Math.abs(percentage - originalPercentage) > 0.01;
        if (percentage > discountDetails.discountLimit && percentage <= discountDetails.discountFreez) {
          setShowOtpSection(true);
          if (isChangingFromExisting || originalPercentage === 0) {
            setOtpVerified(false);
            setOtpSent(false);
          }
        } else {
          setShowOtpSection(false);
          setOtpVerified(false);
        }
      }
    } else if (field === 'amount' && discountDetails) {
      const amount = Number(value);
      if (!isNaN(amount) && state.summary.ornamentAmt > 0) {
        const percentage = (amount / state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher) * 100;
        const isChangingFromExisting = originalPercentage > 0 && Math.abs(percentage - originalPercentage) > 0.01;
        if (percentage > discountDetails.discountLimit && percentage <= discountDetails.discountFreez) {
          setShowOtpSection(true);
          if (isChangingFromExisting || originalPercentage === 0) {
            setOtpVerified(false);
            setOtpSent(false);
          }
        } else {
          setShowOtpSection(false);
          setOtpVerified(false);
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: 'percentage' | 'amount') => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleDiscountSubmit();
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false}>
      <Modal.Header className="border-0 pb-0" closeButton>
        <Modal.Title className="text-secondary fw-normal">
          <i className="bx bx-percentage me-2"></i>
          Discount
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {discountLoading && (
          <div className="alert alert-info mb-3">
            <i className="bx bx-loader-alt bx-spin me-2"></i>
            Loading discount details...
          </div>
        )}

        {state.discount > 0 && !showOtpSection && (
          <div className="alert alert-success mb-3">
            <i className="bx bx-check-circle me-2"></i>
            Current discount: <strong>₹{(state.discount ?? 0).toFixed(2)}</strong>
          </div>
        )}

        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Discount % <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={formData.percentage ?? ""}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.value.length > 3) {
                      target.value = target.value.slice(0, 3);
                    }
                  }}
                  onChange={(e) => updateField('percentage', e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                    handleKeyDown(e, 'percentage')
                  }}
                  isInvalid={!!errors.percentage}
                  // max={discountDetails?.discountFreez || 100}
                  max={100}
                  autoFocus
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={inputMethod === 'amount'}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.percentage}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Discount Amount <span className="text-danger">*</span>
                </Form.Label>
                {/* <Form.Control
                  type="number"
                  placeholder="0.00"
                  value={formData.amount ?? ""}
                  onChange={(e) => updateField('amount', e.target.value)}
                  onKeyDown={(e) => {
                    const val = (e.currentTarget.value || "").toString();
                    if (
                      val.length >= 10 &&
                      /[0-9]/.test(e.key) &&
                      e.currentTarget.selectionStart === e.currentTarget.selectionEnd
                    ) {
                      e.preventDefault();
                    }
                    if (
                      !/[0-9]/.test(e.key) &&
                      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                    handleKeyDown(e, 'amount')
                  }}
                  isInvalid={!!errors.amount}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  min="0"
                  max={state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher}
                  disabled={inputMethod === 'percentage'}
                /> */}
                <Form.Control
                  type="text" // better for validation control
                  placeholder="0.00"
                  value={formData.amount ?? ""}
                  onChange={(e) => {
                    let val = e.target.value;

                    if (/^\d*\.?\d{0,2}$/.test(val) || val === "") {
                      updateField("amount", val);
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData("text").trim();

                    let clean = pasted.replace(/[^0-9.]/g, "");

                    if (clean.split(".").length > 2) {
                      clean = clean.replace(/\.+$/, "");
                    }
                    if (/^\d*\.?\d{0,2}$/.test(clean)) {
                      updateField("amount", clean);
                    }
                  }}
                  onDrop={(e) => e.preventDefault()} // prevent drag-drop invalid input
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                    ];

                    if (!/[0-9.]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }

                    if (e.key === "." && e.currentTarget.value.includes(".")) {
                      e.preventDefault();
                    }
                    const [integerPart] = e.currentTarget.value.split(".");
                    if (
                      integerPart.length >= 10 &&
                      /[0-9]/.test(e.key) &&
                      e.currentTarget.selectionStart === e.currentTarget.selectionEnd
                    ) {
                      e.preventDefault();
                    }

                    handleKeyDown(e, "amount");
                  }}
                  isInvalid={!!errors.amount}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  min="0"
                  max={state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher}
                  disabled={inputMethod === 'percentage'}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Max: ₹{(state.summary.ornamentAmt - state.summary.consumerOffer - state.summary.discountVoucher).toLocaleString()}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {showOtpSection && !otpVerified && (
            <>
              {!otpSent ? (
                <div className="mb-3">
                  <div className="alert alert-warning">
                    <i className="bx bx-info-circle me-2"></i>
                    <strong>OTP Verification Required</strong>
                    <p className="mb-2 mt-1">
                      Discount {inputMethod === 'percentage'
                        ? `${formData.percentage}%`
                        : `₹${formData.amount} (${((Number(formData.amount) / state.summary.ornamentAmt) * 100).toFixed(2)}%)`
                      }
                      exceeds the standard limit of {discountDetails?.discountLimit.toFixed(2)}%
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSendOtp}
                      disabled={otpApiLoading}
                    >
                      {otpApiLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-mobile me-1"></i>
                          Send OTP to {phoneNumber}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="alert alert-success mb-3">
                    <i className="bx bx-check-circle me-2"></i>
                    <strong>OTP Sent Successfully!</strong>
                    <p className="mb-0 mt-1">
                      Please enter the 6-digit OTP sent to {phoneNumber}
                    </p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-secondary">
                      Enter OTP <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={formData.otp}
                      onChange={(e) => updateField('otp', e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && formData.otp.length === 6) {
                          handleOtpVerification();
                        }
                      }}
                      isInvalid={!!errors.otp}
                      maxLength={6}
                      autoFocus
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.otp}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="mb-3">
                    {resendTimer > 0 ? (
                      <div className="text-muted small">
                        <i className="bx bx-time me-1"></i>
                        Resend OTP in {resendTimer} seconds
                      </div>
                    ) : (
                      <Button
                        variant="link"
                        className="p-0 text-primary"
                        onClick={handleResendOtp}
                        disabled={otpApiLoading}
                      >
                        <i className="bx bx-refresh me-1"></i>
                        Resend OTP
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {inputMethod && discountDetails && (
            (() => {
              const percentage = inputMethod === 'percentage'
                ? Number(formData.percentage)
                : (Number(formData.amount) / state.summary.ornamentAmt) * 100;

              if (percentage > discountDetails.discountLimit && percentage <= discountDetails.discountFreez && !otpVerified) {
                return (
                  <Alert variant="warning">
                    <i className="bx bx-error-circle me-2"></i>
                    OTP verification required for discount above {discountDetails.discountLimit.toFixed(2)}%
                  </Alert>
                );
              } else if (percentage > 0 && percentage <= discountDetails.discountLimit) {
                return (
                  <Alert variant="info">
                    <i className="bx bx-info-circle me-2"></i>
                    Discount within allowed limit ({discountDetails.discountLimit.toFixed(2)}%)
                  </Alert>
                );
              } else if (percentage > discountDetails.discountFreez) {
                return (
                  null
                  // <Alert variant="danger">
                  //   {/* <i className="bx bx-error-circle me-2"></i>
                  //   Discount exceeds maximum allowed percentage ({discountDetails.discountFreez.toFixed(2)}%) */}
                  // </Alert>
                );
              }
              return null;
            })()
          )}

          {otpVerified && (
            <Alert variant="success">
              <i className="bx bx-check-circle me-2"></i>
              Discount verified and applied!
            </Alert>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        {state.discount > 0 && (
          <Button variant="warning" onClick={handleRemove}>
            <i className="bx bx-trash me-1"></i>
            Remove Discount
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};